
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AgentCapability, agent_capability_type } from '@/types/agentic';
import { AppRole } from '@/types/supabase';

// Permission level type from the database
export type CapabilityPermissionLevel = 'view' | 'execute' | 'edit' | 'create';

/**
 * Check if the current user has permission to perform an action on a capability
 * @param capabilityId The ID of the capability to check
 * @param permissionLevel The permission level to check (view, execute, edit, create)
 * @returns Promise<boolean> indicating if the user has the permission
 */
export const hasCapabilityPermission = async (
  capabilityId: string,
  permissionLevel: CapabilityPermissionLevel
): Promise<boolean> => {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    
    // Call the database function that checks permissions
    const { data, error } = await supabase.rpc('has_capability_permission', {
      _user_id: user.id,
      _capability_id: capabilityId,
      _permission_level: permissionLevel
    });
    
    if (error) {
      console.error('Error checking capability permission:', error);
      return false;
    }
    
    return data || false;
  } catch (error) {
    console.error('Error in hasCapabilityPermission:', error);
    return false;
  }
};

/**
 * Check if the current user has permission to perform an action on a capability type
 * @param capabilityType The capability type to check
 * @param permissionLevel The permission level to check (view, execute, edit, create)
 * @returns Promise<boolean> indicating if the user has the permission
 */
export const hasCapabilityTypePermission = async (
  capabilityType: agent_capability_type,
  permissionLevel: CapabilityPermissionLevel
): Promise<boolean> => {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    
    // Get user's roles
    const { data: rolesData, error: rolesError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id);
    
    if (rolesError) {
      console.error('Error fetching user roles:', rolesError);
      return false;
    }
    
    const userRoles = rolesData.map(r => r.role);
    
    // Check if any of the user's roles have permission for this capability type
    const { data, error } = await supabase
      .from('capability_permissions')
      .select('*')
      .in('role', userRoles)
      .eq('capability_type', capabilityType)
      .eq('permission_level', permissionLevel)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking capability type permission:', error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Error in hasCapabilityTypePermission:', error);
    return false;
  }
};

/**
 * Update the permissions for a specific capability or capability type
 */
export const updateCapabilityPermission = async (
  role: AppRole, // Change from string to AppRole
  permissionLevel: CapabilityPermissionLevel,
  capabilityId?: string,
  capabilityType?: agent_capability_type
): Promise<boolean> => {
  try {
    if (!capabilityId && !capabilityType) {
      throw new Error('Either capabilityId or capabilityType must be provided');
    }
    
    if (capabilityId && capabilityType) {
      throw new Error('Only one of capabilityId or capabilityType should be provided');
    }
    
    const permissionData = {
      role,
      permission_level: permissionLevel,
      capability_id: capabilityId,
      capability_type: capabilityType
    };
    
    // Upsert the permission record
    const { error } = await supabase
      .from('capability_permissions')
      .upsert(permissionData, {
        onConflict: capabilityId 
          ? 'role,capability_id,permission_level' 
          : 'role,capability_type,permission_level',
        ignoreDuplicates: false
      });
    
    if (error) {
      console.error('Error updating capability permission:', error);
      toast.error('Failed to update permission');
      return false;
    }
    
    toast.success('Permission updated successfully');
    return true;
  } catch (error) {
    console.error('Error in updateCapabilityPermission:', error);
    toast.error('Failed to update permission');
    return false;
  }
};

/**
 * Remove a permission for a specific capability or capability type
 */
export const removeCapabilityPermission = async (
  role: AppRole, // Change from string to AppRole
  permissionLevel: CapabilityPermissionLevel,
  capabilityId?: string,
  capabilityType?: agent_capability_type
): Promise<boolean> => {
  try {
    if (!capabilityId && !capabilityType) {
      throw new Error('Either capabilityId or capabilityType must be provided');
    }
    
    let query = supabase
      .from('capability_permissions')
      .delete()
      .eq('role', role)
      .eq('permission_level', permissionLevel);
    
    if (capabilityId) {
      query = query.eq('capability_id', capabilityId);
    } else if (capabilityType) {
      query = query.eq('capability_type', capabilityType);
    }
    
    const { error } = await query;
    
    if (error) {
      console.error('Error removing capability permission:', error);
      toast.error('Failed to remove permission');
      return false;
    }
    
    toast.success('Permission removed successfully');
    return true;
  } catch (error) {
    console.error('Error in removeCapabilityPermission:', error);
    toast.error('Failed to remove permission');
    return false;
  }
};
